import React from 'react';
import moment from 'moment';
import { Checkbox, TableCell, TableRow, Tooltip, Box, Button, IconButton } from '@mui/material';
import { Restore as RestoreIcon, Visibility as VisibilityIcon, Edit as EditIcon, Delete as DeleteIcon, OpenInNew as OpenInNewIcon, DragIndicator as DragIndicatorIcon } from "@mui/icons-material";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatterCurrency } from '../../../utils';
import LessMoreComp from '../../../shared_elements/components/CustomLessMoreComp';
import { AttachFile } from '@mui/icons-material';
import { PermissionWrapper } from '../../../wrappers';
import { useSelector } from 'react-redux';
import UploadingIcon from '../../../shared_elements/components/UploadingIcon';
import { trackActivity } from '../../../utils/mixpanel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AuditPopover from '../../../shared_elements/components/popover';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { dateMonthYearDateFormat } from '../../../constants';
import { hanwahaInstance } from '../../../shared_elements';

const LeaseLabel = ({ label, statusChange, }) => {
  const statusMap = {
    active: {
      label: "Active",
      color: "#5ec264",
      backgroundImage: "linear-gradient(90deg,#beed87,#d2e9b6 95%)",
    },
    inactive: {
      label: "Inactive",
      color: "#ff9800",
      backgroundImage: "linear-gradient(269deg,#fadfb8,#fdd79e 95%)",
    },
  };
  const statusKey = label?.toLowerCase?.();
  const status = statusMap[statusKey] || { label: label || "-", color: "#000" };
  const { permission } = useSelector((state) => state.leases);
  const modulePermissions = permission?.permissions?.lease_revamp_application?.lease_revamp?.['reserve_rates'];

  return (<>
    {modulePermissions?.includes('U') ?
      <Button
        variant="contained"
        onClick={() => { statusChange(); trackActivity('Item Clicked', { page_title: `Reserve Rates`, page_type: 'Reserve Rate Status Button', }) }}
        endIcon={<ArrowDropDownIcon />}
        sx={{
          backgroundImage: status.backgroundImage,
          color: status.color,
          textTransform: "capitalize",
          padding: "0 6px",
          fontWeight: "600",
          fontSize: "13px",
          whiteSpace: "nowrap",
          justifyContent: "center",
          '& .MuiButton-endIcon': { marginLeft: '0px' }
        }}
      >
        {label}
      </Button> :
      <Button
        variant="contained"
        sx={{
          backgroundImage: status.backgroundImage,
          color: status.color,
          textTransform: "capitalize",
          padding: "0 6px",
          fontWeight: "600",
          fontSize: "13px",
          whiteSpace: "nowrap",
          justifyContent: "center",
          cursor: 'default',
        }}
      >
        {label}
      </Button>}
  </>
  );
};

const ReserveRateList = ({ uploadedFileData, slug, uploadFileProgressData, viewFileStatus, toggleBulkOps, viewRevisonHistory, item, onEdit, onDelete, onView, viewFile, bulk, statusChange, leaseBasicDetails, onEscalationView, isQbEnabled }) => {
  const isInActive = item?.status === true ? false : true;
  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: `Reserve Rates Listing`, page_type: page_type, })
  }
  const { setNodeRef, transform, transition, attributes, listeners } = useSortable({ id: item.slug });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const lgGroupingText =item?.lg_allocations?.length ? item?.lg_allocations?.map((item, index) => `LG${index + 1}: ${item?.percentage?.toFixed(2)}%`)?.join(" • ") : ""

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell className="table-actions-list">
        <Box className="icon-container" style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "reserve_rates", operationType: ['D'] }}>
            {leaseBasicDetails?.is_reserve_rate === 0 ?
              <Checkbox color='primary' size='small' checked={bulk?.ids.find(items => items?.id === item?.id) ? true : false} onChange={(e) => toggleBulkOps(e.target.checked, 'single', item)} />
              : null}
          </PermissionWrapper>
          <AuditPopover item={item}>
            <MoreVertIcon fontSize="small" color="primary" sx={{ cursor: "pointer" }} />
          </AuditPopover>
          <Tooltip title="Revision History">
            <RestoreIcon className='revision-history' fontSize="small" color="primary" onClick={() => { viewRevisonHistory(); handleItemClicked('RevisionHistoryIcon') }} sx={{ cursor: "pointer" }} />
          </Tooltip>
          <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "reserve_rates", operationType: ['R'] }}>
            <Tooltip title="View">
              <VisibilityIcon fontSize="small" color="primary" onClick={() => { onView(); handleItemClicked('ViewIcon') }} sx={{ cursor: "pointer" }} />
            </Tooltip>
          </PermissionWrapper>
          <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "reserve_rates", operationType: ['U'] }}>
            {item?.status ? <Tooltip title="Edit">
              <EditIcon fontSize="small" color="primary" onClick={() => { onEdit(); handleItemClicked('EditIcon') }} sx={{ cursor: "pointer" }} />
            </Tooltip> : null}
          </PermissionWrapper>
          <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "reserve_rates", operationType: ['D'] }}>
            {leaseBasicDetails?.is_reserve_rate === 0 ? <Tooltip title="Delete">
              <DeleteIcon fontSize="small" color="error" onClick={() => { onDelete(); handleItemClicked('DeleteIcon') }} sx={{ cursor: "pointer" }} />
            </Tooltip> : null}
          </PermissionWrapper>
          <Tooltip title="Drag to reorder">
            <DragIndicatorIcon fontSize="small" sx={{ cursor: 'grab', color: '#aaa', flexShrink: 0, touchAction: 'none', outline: 'none', border: 'none', '&:focus': { outline: 'none' }, '&:active': { outline: 'none' } }} {...attributes} {...listeners} />
          </Tooltip>
          <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "reserve_rates", operationType: ['R'] }}>
            {
              item?.attachments?.length > 0 && (
                <Tooltip title="Attached File(s)">
                  <Box className="attachment-container">
                    <AttachFile fontSize="small" color="primary" onClick={() => { viewFile(); handleItemClicked('AttachmentsIcon') }} sx={{ cursor: "pointer" }} />
                    <Box className="attachment-count"> ({item.attachments.length}) </Box>
                  </Box>
                </Tooltip>
              )
            }
          </PermissionWrapper>
          {(slug === item?.slug) && <UploadingIcon viewFileStatus={() => viewFileStatus(uploadedFileData)} />}
        </Box>
      </TableCell>
      <TableCell>
        <LeaseLabel
          label={item?.status === true ? "Active" : "Inactive"}
          statusChange={() => statusChange(item)}
        />
      </TableCell>
      <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer' }} onClick={() => {onView(); handleItemClicked();}}><LessMoreComp name={item?.name} lessMr='name' /></TableCell>
      <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer' }} onClick={() => {onView(); handleItemClicked();}}>{item?.reserve_rate_type?.label || "--"}</TableCell>
      <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer' }} onClick={() => {onView(); handleItemClicked();}}>{item?.account_type?.name || '--'}</TableCell>
      {item?.is_grouped_lgs &&   lgGroupingText ?     <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit' }} >{lgGroupingText || '--'}</TableCell> :       <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer' }} onClick={() => {onView(); handleItemClicked();}}>{item?.major_assembly?.name || '--'}</TableCell>}
      <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer' }} onClick={() => {onView(); handleItemClicked();}}>{item?.reserve_rate_based_on?.label ? item?.reserve_rate_based_on?.label : item?.interval?.label || '--'}</TableCell>
      <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer' }} onClick={() => {onView(); handleItemClicked();}}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {item?.reserve_rate_type?.value === 1 || (item?.reserve_rate_type?.value === 2 && item?.fixed_rate ) ? <span>
            {`${formatterCurrency(
              item?.currency?.code
            ).format(item?.reserve_rate_per_interval || 0)}`}
          </span> : <Button
            variant="contained"
            sx={{
              color: "primary",
              textTransform: "capitalize",
              padding: "0 6px",
              fontWeight: "600",
              fontSize: "13px",
              whiteSpace: "nowrap",
              justifyContent: "center",
              cursor: 'pointer',
            }}
            onClick={() => { onView(); handleItemClicked('View Rate Button') }}
          >
            View Rate
          </Button>}
          {(item?.table_based_esc) || (!item?.table_based_esc && item?.rate_escalation_percentage && item?.rate_escalation_start_date && item?.rate_escalation_interval) ? <Tooltip title="View Escalated Rate" arrow placement="bottom" ><IconButton
            size="small"
            color="primary"
            onClick={() => {
              onEscalationView();
              handleItemClicked("Escalation Rate Icon");
            }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton> </Tooltip> : null}
        </Box>

      </TableCell>

      <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer' }} onClick={() => {onView(); handleItemClicked();}}>{item?.rate_start_date ? moment(item.rate_start_date).format(dateMonthYearDateFormat) : "--"}</TableCell>
      <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer' }} onClick={() => {onView(); handleItemClicked();}}>{item?.rate_end_date ? moment(item.rate_end_date).format(dateMonthYearDateFormat) : "--"}</TableCell>
      <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer' }} onClick={() => {onView(); handleItemClicked();}}>{item?.rate_escalation_percentage ? `${item?.rate_escalation_percentage}%` : '--'}</TableCell>
      <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer' }} onClick={() => {onView(); handleItemClicked();}}>{item?.rate_escalation_interval?.label || '--'}</TableCell>
      <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer' }} onClick={() => {onView(); handleItemClicked();}}>{item?.rate_escalation_start_date ? moment(item?.rate_escalation_start_date).format(dateMonthYearDateFormat) : '--'}</TableCell>
      {isQbEnabled && <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer' }} onClick={() => {onView(); handleItemClicked();}}>{item?.qb_product_and_service_id?.name || '--'}</TableCell>}
      {isQbEnabled && <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer' }} onClick={() => {onView(); handleItemClicked();}}>{item?.qb_chart_of_account_id?.name || '--'}</TableCell>}
            {isQbEnabled && hanwahaInstance && <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer'}} onClick={() => {onView(); handleItemClicked();}}>{item?.qb_class_id?.name || '--'}</TableCell>}
      {isQbEnabled && hanwahaInstance && <TableCell sx={{ color: isInActive ? '#00000061 !important' : 'inherit', cursor: 'pointer' }} onClick={() => {onView(); handleItemClicked();}}>{item?.qb_sub_class_id?.name || '--'}</TableCell>}
      {/* <TableCell>{item?.starting_fund_amount ? `${!item?.currency === "USD" ? item?.currency : "US"}${formatterCurrency().format(item?.starting_fund_amount)}` : '--'}</TableCell> */}

    </TableRow>
  );
};

export default ReserveRateList;